import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {MetaData} from '@models/adminstration/meta-data';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {TranslateService} from '@ngx-translate/core';
import {NotificationService} from '@services/notification/notification.service';
import {MetadataElasticSearchService} from '@services/elastic-search/metadata/metadata-elastic-search.service';
import {Subject} from 'rxjs';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {BIGQUERY_VALID_NAME_REGEX} from '@constants/reg-exp';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {ELASTIC_SEARCH_TYPE} from '@constants/elastic-search/elastic-search-type.enum';
import {ElasticSearchResponseEntity} from '@models/elastic-search/response/elastic-search-response-entity';

@Component({
  selector: 'scitis-admin-metadata-add',
  templateUrl: './metadata-edit.component.html',
  styleUrl: './metadata-edit.component.scss',
  standalone: false
})
export class MetadataEditComponent implements OnInit, OnDestroy {

  @Input()
  metadata: MetaData = new MetaData();

  @Input()
  type: ELASTIC_SEARCH_TYPE.GATEWAY_META_DATA | ELASTIC_SEARCH_TYPE.MACHINE_META_DATA = ELASTIC_SEARCH_TYPE.MACHINE_META_DATA;

  @Input()
  parentId: number | string = '';

  @Input()
  parentName: string = '';

  @Input()
  isNewMetaData: boolean = false;

  submitted: boolean = false;
  metadataForm: UntypedFormGroup = new UntypedFormGroup({});
  private destroy$: Subject<boolean> = new Subject<boolean>();

  private readonly PageName: string = 'metadata-edit';

  constructor(
      private activeModal: NgbActiveModal,
      private translateService: TranslateService,
      private notificationService: NotificationService,
      private metadataElasticService: MetadataElasticSearchService,
      private loadingIndicator: LoadingIndicatorService) {

  }

  // convenience getter for easy access to form fields
  get f() {
    return this.metadataForm.controls;
  }

  get isDisabledSaveBtn(): boolean {
    const isFormValid = this.metadataForm.invalid;
    return this.submitted ? isFormValid : this.submitted && isFormValid;
  };

  ngOnInit(): void {
    this.generateMetadataForm(this.metadata);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onClose(): void {
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  onCancel(): void {
    this.activeModal.dismiss(ModalDialogDismissOption.CANCEL);
  }

  onSave(): void {

    this.submitted = true;
    // stop here if form is invalid
    if (this.metadataForm.invalid) {
      return;
    }

    const key: string = this.f.key?.value?.trim() ?? '';
    this.loadingIndicator.show(this.translateService.instant('Verify metadata key existence ...'));

    this.metadataElasticService.listByKey(key, this.parentId, this.type).subscribe(
        {
          next: (success: ElasticSearchResponseEntity) => {

            const foundMetadata: MetaData[] = success.result?.data || [];
            const compareId: number = this.metadata?.id ?? -1;
            const sameKeyDifferentId: MetaData[] = foundMetadata.filter(m => m.id !== compareId);
            if (!sameKeyDifferentId || sameKeyDifferentId.length === 0) {
              this.updateMetaDataProperties();
              this.activeModal.close(this.metadata);
              this.loadingIndicator.hide();
              return;
            }

            const message = this.translateService.instant('There is already a %type item with key "%key" for %parent.')
            .replaceAll('%type', this.type)
            .replaceAll('%key', this.metadata?.key?.toBold())
            .replaceAll('%parent', this.parentName);
            this.notificationService.warning(message, this.PageName);
            this.loadingIndicator.hide();
            return;

          },
          error: (error) => {
            const message = this.translateService.instant('Error verifying %type %key existence for %parent.')
            .replaceAll('%type', this.type)
            .replaceAll('%key', this.metadata.key)
            .replaceAll('%parent', this.parentName);
            this.notificationService.error(message, error, this.PageName, true);
            this.loadingIndicator.hide();
          }
        }
    );
  }

  private generateMetadataForm(metadata: MetaData): void {
    this.metadataForm = new UntypedFormGroup({
      key: new UntypedFormControl({value: metadata.key, disabled: !this.isNewMetaData}, [Validators.required, Validators.pattern(BIGQUERY_VALID_NAME_REGEX)]),
      value: new UntypedFormControl(metadata.value),
    });
  }

  private updateMetaDataProperties(): void {
    this.metadata.key = this.f?.key?.value?.trim() ?? '';
    this.metadata.value = this.f?.value?.value ?? '';
  }

  protected onKeyChanged() {

    this.f.key.setErrors({used: null});
    this.f.key.updateValueAndValidity();
    const key: string = this.f.key?.value?.trim() ?? '';
    this.metadataElasticService.listByKey(key, this.parentId, this.type).subscribe(
        {
          next: (success: ElasticSearchResponseEntity) => {
            const foundMetadata: MetaData[] = success.result?.data || [];
            const compareId: number = this.metadata?.id ?? -1;
            const sameKeyDifferentId: MetaData[] = foundMetadata.filter(m => m.id !== compareId);
            if (!sameKeyDifferentId || sameKeyDifferentId.length === 0) {
              this.f.key.setErrors({used: null});
              this.f.key.updateValueAndValidity();
            } else {
              this.f.key.setErrors({used: true});
            }
          },
          error: (_) => {
            this.f.key.setErrors({used: true});
          }
        }
    );
  }
}